/**
 * The ONE storage seam (M2 spec §5.1).
 *
 * `cc-learning-app` (React Native) is being restructured on this architecture,
 * so nothing in `src/Store/liveGame/**`, `pages/home/models/**`,
 * `pages/home/lobbyStore.ts` or `resolveLobbyVariant.ts` may name `window`,
 * `document`, `localStorage`, `sessionStorage` or `localForage`. They all come
 * here instead; the app ships its own copy of THIS file (AsyncStorage / MMKV)
 * and everything above it is unchanged. `__tests__/platformFree.test.ts`
 * enforces the rule.
 *
 * There are TWO areas, because the web already has two stores and they are not
 * interchangeable:
 *
 *  - the APP store (`getItem` / `setItem` / `removeItem`) — async and
 *    object-capable, backed by the localForage helpers in `@/Utils/CommonUtils`.
 *    This is where the handshake keys OTHER screens write live
 *    (`challengePacket`, `league_*`, `club_*`, `lastGameTimeControl`), so it
 *    must stay the same physical store they use. On RN it becomes
 *    AsyncStorage.
 *  - PREFS (`getPrefSync` / `setPref` / `removePref`) — small strings this
 *    surface owns and has to read BEFORE the first paint (`play_v2_theme`, the
 *    lobby-variant override, the lobby's tab/option memory). A theme or a
 *    variant resolved one tick late is a visible flash, so this half is
 *    synchronous; on web it is `localStorage`. A platform with no synchronous
 *    store returns null from `getPrefSync`, and every caller already has a
 *    working default for that.
 *  - SESSION (`getSessionSync` / `setSession`) — per-tab, dies with the tab
 *    (`sessionStorage` on web, an in-memory map anywhere else).
 *
 * Every function swallows its errors: private mode, SSR and a disabled
 * IndexedDB must degrade to "the choice does not survive the reload", never to
 * a thrown render.
 */
type AppStore = {
  getLocalStorage: (name: string) => Promise<any>;
  setLocalStorage: (name: string, value: any) => Promise<void>;
  removeLocalStorage: (name: string) => Promise<boolean>;
};

/**
 * The app store is reached through a LAZY require, deliberately — but NOT as a
 * bundle-size measure: a `require()` inside a function is NOT a webpack chunk
 * boundary, so `@/Utils/CommonUtils` and everything it drags in (navigation, the
 * query client, localForage) still land in the same chunk as this file.
 *
 * What the hop really buys is EXECUTION ORDER, and that is why it stays:
 * `CommonUtils` is only evaluated the first time a storage call is made, so
 * importing this module (every `View.tsx` in the V2 tree does, transitively,
 * through `useV2Theme` → `getPrefSync`) does not run that module graph's
 * side effects. In particular a View unit test can mount without mocking
 * localForage, navigation and the query client.
 *
 * `require` rather than `await import(...)`: the jest transform leaves a
 * dynamic import alone, so it throws at runtime under the test runner.
 */

const appStore = (): AppStore => require('@/Utils/CommonUtils');

/** The async, object-capable app store (localForage on web). */
export async function getItem<T = any>(key: string): Promise<T | null> {
  try {
    const {getLocalStorage} = appStore();
    const value = await getLocalStorage(key);
    return (value as T) ?? null;
  } catch (e) {
    return null;
  }
}

/**
 * NOTE: `setLocalStorage` ignores a falsy value (CommonUtils L92), which is
 * exactly how PlayChess nulls `challengePacket` — the behaviour is mirrored,
 * not fixed, so the handshake stays byte-compatible with the old screen.
 */
export async function setItem(key: string, value: any): Promise<void> {
  try {
    const {setLocalStorage} = appStore();
    await setLocalStorage(key, value);
  } catch (e) {
    // storage unavailable — the value simply does not survive the reload
  }
}

export async function removeItem(key: string): Promise<boolean> {
  try {
    const {removeLocalStorage} = appStore();
    return await removeLocalStorage(key);
  } catch (e) {
    return false;
  }
}

/**
 * The web's synchronous preference store. Typed as `any` on purpose: this file
 * is the only place in the V2 tree allowed to name a web global, and the RN
 * copy of it names none.
 */
const webStore = (which: 'localStorage' | 'sessionStorage'): any => {
  try {
    const g: any = globalThis as any;
    return g?.[which] ?? null;
  } catch (e) {
    return null;
  }
};

export function getPrefSync(key: string): string | null {
  try {
    return webStore('localStorage')?.getItem(key) ?? null;
  } catch (e) {
    return null;
  }
}

export function setPref(key: string, value: string): void {
  try {
    webStore('localStorage')?.setItem(key, value);
  } catch (e) {
    // private mode — the preference is not sticky
  }
}

export function removePref(key: string): void {
  try {
    webStore('localStorage')?.removeItem(key);
  } catch (e) {
    // private mode — nothing to remove
  }
}

export function getSessionSync(key: string): string | null {
  try {
    return webStore('sessionStorage')?.getItem(key) ?? null;
  } catch (e) {
    return null;
  }
}

export function setSessionPref(key: string, value: string): void {
  try {
    webStore('sessionStorage')?.setItem(key, value);
  } catch (e) {
    // private mode — the per-tab flag is not sticky
  }
}

export default {
  getItem,
  setItem,
  removeItem,
  getPrefSync,
  setPref,
  removePref,
  getSessionSync,
  setSessionPref,
};
